import React, { useEffect, useState } from 'react';
import { IonButton, IonInput, IonItem, IonLabel, IonList, IonModal, IonNote, IonSearchbar, IonSelect, IonSelectOption } from '@ionic/react';
import { DuplicateMatch, ServiceCustomer } from '../../../interfaces/ServiceEstimate';
import { ApiError, serviceApi } from '../../../utils/serviceEstimatesApi';
import './CustomerPicker.css';

interface Props {
  value: ServiceCustomer | null;
  disabled?: boolean;
  onChange: (c: ServiceCustomer | null) => void;
}

const EMPTY: Partial<ServiceCustomer> = { name: '', contact_name: '', contact_email: '', contact_phone: '', address: '', city: '', state: 'IL', zip_code: '' };

/** Search the service customer list; add a new one only after the duplicate check. */
const CustomerPicker: React.FC<Props> = ({ value, disabled, onChange }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<ServiceCustomer[]>([]);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Partial<ServiceCustomer>>(EMPTY);
  const [matches, setMatches] = useState<DuplicateMatch[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2) {
      setResults([]);
      return undefined;
    }
    const t = setTimeout(() => {
      serviceApi.searchCustomers(q.trim()).then(setResults).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const pick = (c: ServiceCustomer) => {
    onChange(c);
    setQ('');
    setResults([]);
    setOpen(false);
    setMatches([]);
  };

  const save = async (force: boolean) => {
    if (!draft.name?.trim()) {
      setError('Enter the customer name.');
      return;
    }
    if (!draft.address?.trim() || !draft.city?.trim() || !draft.zip_code?.trim()) {
      setError('Enter the job site address, city and ZIP so the office can check for duplicates.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      pick(await serviceApi.createCustomer(draft, force));
      setDraft(EMPTY);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) setMatches((e.data.matches as DuplicateMatch[]) || []);
      else setError(e instanceof Error ? e.message : 'Could not save the customer.');
    } finally {
      setBusy(false);
    }
  };

  const field = (k: keyof ServiceCustomer) => (e: CustomEvent) => setDraft({ ...draft, [k]: String(e.detail.value ?? '') });

  return (
    <div className="customer-picker">
      {value ? (
        <div className="customer-picker-chosen">
          <div>
            <strong>{value.name}</strong>
            {value.status === 'pending' && <IonNote color="warning"> · new, waiting for office review</IonNote>}
            <div className="customer-picker-meta">{[value.address, value.city, value.state].filter(Boolean).join(', ')}</div>
          </div>
          {!disabled && (
            <IonButton fill="outline" size="small" onClick={() => onChange(null)}>
              Change
            </IonButton>
          )}
        </div>
      ) : (
        <>
          <IonSearchbar value={q} debounce={0} placeholder="Search customers by name, address or email" onIonInput={(e) => setQ(String(e.detail.value ?? ''))} disabled={disabled} />
          {results.length > 0 && (
            <IonList inset>
              {results.map((c) => (
                <IonItem key={c.id} button onClick={() => pick(c)} detail={false}>
                  <IonLabel>
                    <h3>{c.name}</h3>
                    <p>{[c.address, c.city, c.state].filter(Boolean).join(', ') || c.contact_email}</p>
                  </IonLabel>
                  {c.tax_location_label && <IonNote slot="end">{c.tax_location_label}</IonNote>}
                </IonItem>
              ))}
            </IonList>
          )}
          <IonButton fill="clear" size="small" onClick={() => { setDraft({ ...EMPTY, name: q }); setOpen(true); }} disabled={disabled}>
            + New customer
          </IonButton>
        </>
      )}

      <IonModal isOpen={open} onDidDismiss={() => { setOpen(false); setMatches([]); setError(''); }}>
        <div className="customer-picker-modal">
          <h2>New customer</h2>
          {matches.length === 0 ? (
            <>
              <IonItem><IonInput label="Customer name" labelPlacement="stacked" value={draft.name} onIonInput={field('name')} /></IonItem>
              <IonItem><IonInput label="Contact name" labelPlacement="stacked" value={draft.contact_name} onIonInput={field('contact_name')} /></IonItem>
              <IonItem><IonInput label="Contact email" labelPlacement="stacked" type="email" value={draft.contact_email} onIonInput={field('contact_email')} /></IonItem>
              <IonItem><IonInput label="Contact phone" labelPlacement="stacked" type="tel" value={draft.contact_phone} onIonInput={field('contact_phone')} /></IonItem>
              <IonItem><IonInput label="Job site street address" labelPlacement="stacked" value={draft.address} onIonInput={field('address')} /></IonItem>
              <div className="customer-picker-row">
                <IonItem><IonInput label="City" labelPlacement="stacked" value={draft.city} onIonInput={field('city')} /></IonItem>
                <IonItem>
                  <IonSelect label="State" labelPlacement="stacked" value={draft.state} onIonChange={field('state')}>
                    <IonSelectOption value="IL">IL</IonSelectOption>
                    <IonSelectOption value="WI">WI</IonSelectOption>
                  </IonSelect>
                </IonItem>
                <IonItem><IonInput label="ZIP" labelPlacement="stacked" inputmode="numeric" value={draft.zip_code} onIonInput={field('zip_code')} /></IonItem>
              </div>
              {error && <p className="customer-picker-error">{error}</p>}
              <div className="customer-picker-actions">
                <IonButton onClick={() => save(false)} disabled={busy}>Save customer</IonButton>
                <IonButton fill="outline" onClick={() => setOpen(false)}>Cancel</IonButton>
              </div>
            </>
          ) : (
            <>
              <p>This customer may already be on file. Pick the right one, or create a new customer if none of these match.</p>
              <IonList>
                {matches.map((m) => (
                  <IonItem key={m.id}>
                    <IonLabel>
                      <h3>{m.name}</h3>
                      <p>{[m.address, m.city, m.state].filter(Boolean).join(', ')}</p>
                      <p className="customer-picker-reasons">{m.reasons.join(' · ')}</p>
                    </IonLabel>
                    <IonButton slot="end" onClick={() => pick(m)}>Use this customer</IonButton>
                  </IonItem>
                ))}
              </IonList>
              <div className="customer-picker-actions">
                <IonButton fill="outline" onClick={() => save(true)} disabled={busy}>None of these, create new</IonButton>
                <IonButton fill="clear" onClick={() => setMatches([])}>Back</IonButton>
              </div>
            </>
          )}
        </div>
      </IonModal>
    </div>
  );
};

export default CustomerPicker;
